import { type Ref, ref, shallowRef } from 'vue'

import { Cart } from '@tix/entity'
import type { TrackContext } from '@tix/error-tracker'
import { APIErrorCodes, type Schema, isNotFoundError, isTicketureApiError } from '@tix/openapi-sdk'

import { findDonationTicket, getDonationReserveSelection } from '@/helpers/donationHelpers'
import {
  type DonationOptionsValues,
  buildDonationSurveyData,
  buildDonationSurveyUpdate,
} from '@/helpers/donationSurvey'
import { getCartTrackableData } from '@/helpers/errorContext'
import { mapCheckoutResponse } from '@/helpers/stripeHelpers'

import { errorTracker } from '@/services/errorTracking'

import type { CheckoutIdentityFieldsExposed } from '@/components/CheckoutIdentityFields.vue'
import type { DonationOptionsExposed } from '@/components/donations/DonationOptions.vue'
import type { DonationAmountSelection, DonationCustomAmount } from '@/components/donations/DonationWidget.vue'
import type { CheckoutPreflightResult, PaidCheckoutCartResult } from '@/components/donations/checkout.types'

import { useLatestWinsQueue } from '@/composables/useLatestWinsQueue'

type CheckoutCartBody = {
  gateway_data: { source: string; source_type: string }
  guest_identity_id?: string
  captcha?: { provider: 'hcaptcha'; token: string }
}

export type UseDonationCartSurveyOptions = {
  donationOptions: Ref<DonationOptionsValues>
  donationGroup: Schema<'ticket_group'>
}

type CheckoutIdentityRef = {
  readonly value: CheckoutIdentityFieldsExposed | null | undefined
}

type DonationOptionsRef = {
  readonly value: DonationOptionsExposed | null | undefined
}

type ReservePayload = Schema<'reserve_tickets_request'>

const donationCartEmbeds: Array<'ticket' | 'ticket.ticket_group' | 'ticket.ticket_type' | 'ticket.event_template'> = [
  'ticket',
  'ticket.ticket_group',
  'ticket.ticket_type',
  'ticket.event_template',
]

type AdditionalInfoPayload = NonNullable<ReservePayload['additional_info']>

/**
 * A cart mutation failed because the cart no longer exists server-side: either a
 * 404 (never existed / already gone) or a 410 `cart_expired` (session timed out
 * and the cart was released). Both mean we should drop our reference and let the
 * next action create a fresh cart.
 */
function isCartGoneError(error: Error | null): boolean {
  return isNotFoundError(error) || (isTicketureApiError(error) && error.hasCode(APIErrorCodes.cart_expired))
}

function buildSurveyReserve(
  ticket: Schema<'ticket'>,
  additionalInfo: AdditionalInfoPayload,
  customAmountId: string | undefined,
): ReservePayload {
  // Only variable-price donations carry an explicit face_value. Resending one for a fixed
  // preset would be rejected if that ticket type's price changed since the first reserve.
  const isVariablePrice = ticket.ticket_type_id === customAmountId

  return {
    tickets: [
      {
        ticket_type_id: ticket.ticket_type_id,
        face_value: isVariablePrice ? ticket.face_value : undefined,
      },
    ],
    additional_info: additionalInfo,
  }
}

/**
 * Donation cart controller for a single donation event. Owns the cart state and translates widget intents — preset clicks and custom-amount edits — into serialized cart reservations. Only an empty custom amount clears the cart; invalid non-empty values are ignored so intermediate keystrokes don't drop an existing reservation.
 *
 * Two internal stages sit between an intent and the API: `queue` (latest-wins vs an in-flight request) → `mutation` (the actual `Cart` modify/create call).
 *
 * When {@link survey} is provided, survey answers ride along on amount reserves (defaults / current form state). Toggling options alone does not modify the cart; {@link checkoutPreflight} syncs any remaining diff at pay time.
 *
 * @param customAmount Variable-price ticket type for the "other amount" field, or `null` when absent.
 * @param survey Survey answers included on amount reserves, when the donation collects them.
 * @param checkoutIdentity Checkout identity fields. `validateForm` no-ops until this is mounted.
 * @param donationOptionsRef Donation options fields. Omitted when the donation does not collect them.
 */
export function useDonationCart(
  customAmount: DonationCustomAmount | null,
  survey?: UseDonationCartSurveyOptions,
  checkoutIdentity?: CheckoutIdentityRef,
  donationOptionsRef?: DonationOptionsRef,
) {
  const surveySpec = survey?.donationGroup?.additional_info_spec ?? null
  const ticketGroupId = survey?.donationGroup?.id
  const donationOptions = survey?.donationOptions

  const cart = shallowRef<Cart | null>(null)
  /** Mid-flow cart loss only (410/404 or failed reconcile). */
  const error = ref<'cart-gone' | null>(null)
  /** Set when Pay is clicked before a reserved amount. Cleared once Stripe can mount. */
  const amountInvalid = ref(false)
  /** @internal Last failed select/clear/sync in the mutation queue. Preflight reads this after a drain so amount changes queued during survey sync still fail Pay when the cart was restored (tickets present). */
  let lastMutationError: Error | null = null

  // The selection the UI should show. Set optimistically to the latest intent so
  // the widget highlights instantly, then snapped back to cart truth once the
  // queue drains. This is the single signal the widget reconciles to — a server
  // round-trip that corrects or drops the cart flows through here automatically.
  const optimisticSelection = ref<DonationAmountSelection | null>(null)

  function syncSelectionFromCart(): void {
    const ticket = findDonationTicket(cart.value)
    if (!ticket) {
      optimisticSelection.value = null
      return
    }

    const faceValue = ticket.face_value != null ? Number(ticket.face_value) : undefined
    optimisticSelection.value = { ticketTypeId: ticket.ticket_type_id, faceValue }
  }

  const { pending, enqueue, whenIdle } = useLatestWinsQueue(syncSelectionFromCart)

  // Handles DonationWidget's `selectPreset` event.
  function onSelectPreset(ticketTypeId: string): void {
    selectAmount({ ticketTypeId })
  }

  // Handles DonationWidget's `update:customAmount` event.
  function onCustomAmountUpdate(amount: number | null): void {
    if (amount === null) {
      clearSelection()
      return
    }

    const selection = getDonationReserveSelection(amount, customAmount)
    if (selection) {
      selectAmount(selection)
    }
  }

  async function checkoutPreflight(): Promise<CheckoutPreflightResult> {
    await whenIdle()

    // Drop failures from earlier selects/clears so Pay only sees errors from this preflight drain.
    lastMutationError = null

    if (survey && donationOptions) {
      // enqueue waits for the full drain — including any amount change queued while sync runs — so inspect cart/lastMutationError after drain, not syncSurveyIfNeeded alone.
      await enqueue(async () => {
        await syncSurveyIfNeeded()
      })
    }

    if (lastMutationError) {
      return { status: 'error', error: lastMutationError }
    }

    if (!findDonationTicket(cart.value)) {
      return { status: 'error', error: new Error('Select an amount to continue') }
    }

    return { status: 'success' }
  }

  function validateForm(): string | null | undefined {
    const identity = checkoutIdentity?.value?.getValidatedIdentity()
    if (identity === undefined) return 'Payment session not ready. Try again.'

    const donationOptionsError = donationOptionsRef?.value?.validateAndReturnError()

    const noAmount = !findDonationTicket(cart.value)
    amountInvalid.value = noAmount

    let countOfErrors = 0

    if (donationOptionsError) {
      countOfErrors += parseInt(donationOptionsError)
    }

    if (identity.status === 'error') {
      countOfErrors += parseInt(identity.error)
    }

    if (countOfErrors === 0) {
      return null
    }

    // Only count the amount invalid error if there are other errors.
    // Otherwise, show it as a separate error.
    if (noAmount) {
      countOfErrors += 1
    }

    return countOfErrors === 1 ? '1 field needs your attention.' : `${countOfErrors} fields need your attention.`
  }

  // Cart mutation for selecting an amount.
  function selectAmount(selection: DonationAmountSelection): Promise<void> {
    // Reflect the intent immediately; the drain reconciles it to cart truth.
    optimisticSelection.value = selection
    return enqueue(async () => {
      lastMutationError = null

      // Skips modify when ticket type / face value match the cart. Survey answer changes alone will not re-reserve; checkoutPreflight syncs the diff at pay time.
      if (isCurrentSelection(selection)) {
        return
      }

      const removeId = currentTicketId()
      const additionalInfo = buildAdditionalInfo()
      const reserve: ReservePayload = {
        tickets: [{ ticket_type_id: selection.ticketTypeId, face_value: selection.faceValue?.toFixed(2) }],
        ...(additionalInfo?.length ? { additional_info: additionalInfo } : {}),
      }

      const result = cart.value
        ? await cart.value.modify({
            remove: removeId ? { tickets: [removeId] } : undefined,
            reserve,
            _embed: donationCartEmbeds,
          })
        : await Cart.createAndModify({ reserve, _embed: donationCartEmbeds })

      if (result.status === 'success') {
        cart.value = result.data
        error.value = null
        return
      }

      lastMutationError = result.error
      if (result.error) {
        trackError(result.error, { operation: 'donation.cart.selectAmount', reason: 'select_amount_failure' })
      }
      await reconcileCartAfterFailure(result.error)
    })
  }

  // Cart mutation for clearing the custom amount.
  function clearSelection(): Promise<void> {
    optimisticSelection.value = null
    return enqueue(async () => {
      const removeId = currentTicketId()
      if (!cart.value || !removeId) {
        return
      }

      lastMutationError = null

      const result = await cart.value.modify({
        remove: { tickets: [removeId] },
        _embed: donationCartEmbeds,
      })

      if (result.status === 'success') {
        // Nothing should remain after the donation ticket is removed. If it does, drop the cart so the next amount starts clean.
        if (result.data.tickets && result.data.tickets.length > 0) {
          trackError(new Error('Donation cart failed to clear'), {
            operation: 'donation.cart.clearSelection',
            reason: 'cart_not_empty',
          })
        }
        cart.value = null
        error.value = null
        return
      }

      lastMutationError = result.error
      if (result.error) {
        trackError(result.error, { operation: 'donation.cart.clearSelection', reason: 'clear_selection_failure' })
      }
      await reconcileCartAfterFailure(result.error)
    })
  }

  async function syncSurveyIfNeeded(): Promise<void> {
    const currentCart = cart.value
    const ticket = findDonationTicket(currentCart)
    if (!currentCart || !ticket || !donationOptions) {
      return
    }

    const data = buildDonationSurveyUpdate(
      donationOptions.value,
      surveySpec,
      currentCart.data?.additional_info,
      ticket.ticket_group_id,
    )
    if (!data) {
      return
    }

    lastMutationError = null

    const reserve = buildSurveyReserve(ticket, [{ ticket_group_id: ticket.ticket_group_id, data }], customAmount?.id)
    const removeId = ticket.id
    const result = await currentCart.modify({
      remove: { tickets: [removeId] },
      reserve,
      _embed: donationCartEmbeds,
    })

    if (result.status === 'success') {
      cart.value = result.data
      error.value = null
      return
    }

    lastMutationError = result.error
    if (result.error) {
      trackError(result.error, { operation: 'donation.cart.syncSurvey', reason: 'sync_survey_failure' })
    }
    await reconcileCartAfterFailure(result.error)
  }

  async function reconcileCartAfterFailure(operationError: Error | null): Promise<void> {
    const id = cart.value?.id
    if (!id) {
      return
    }

    if (isCartGoneError(operationError)) {
      cart.value = null
      error.value = 'cart-gone'
    } else {
      const refresh = await Cart.loadMy(id, { _embed: donationCartEmbeds })

      if (refresh.status === 'success') {
        cart.value = refresh.data
        error.value = null
      } else {
        cart.value = null
        error.value = 'cart-gone'
      }
    }

    // Checkout reconciles outside the mutation queue, so sync here (queue drain already covers modify/clear).
    syncSelectionFromCart()
  }

  function isCurrentSelection(selection: DonationAmountSelection): boolean {
    const ticket = findDonationTicket(cart.value)
    if (!ticket || ticket.ticket_type_id !== selection.ticketTypeId) {
      return false
    }

    if (selection.faceValue == null) {
      return true
    }

    return Number(ticket.face_value) === selection.faceValue
  }

  function currentTicketId(): string | undefined {
    return findDonationTicket(cart.value)?.id
  }

  function buildAdditionalInfo(): AdditionalInfoPayload | undefined {
    if (!ticketGroupId || !surveySpec || !donationOptions) {
      return undefined
    }

    const data = buildDonationSurveyData(donationOptions.value, surveySpec)
    if (!data) {
      return undefined
    }

    return [{ ticket_group_id: ticketGroupId, data }]
  }

  /**
   * Two-stage Stripe settle via Cart shopper checkout (`checkoutMy` / `guestCheckoutMy`). Signature matches {@link StripePayment} `checkoutCart` prop.
   */
  async function checkoutCart(body: CheckoutCartBody): Promise<PaidCheckoutCartResult> {
    // The pay button should be disabled when pending, so this is just a bit more defensive.
    if (pending.value) {
      const error = new Error('Cannot checkout — cart update in progress')
      trackError(error, { operation: 'donation.cart.checkout', reason: 'pending' })
      return { status: 'error', error, tracked: true }
    }

    const current = cart.value
    if (!current) {
      // Reachable if amount is cleared (or cart dropped) between preflight and checkoutCart; Pay should freeze the widget while submitting.
      const error = new Error('Cannot checkout — no cart')
      trackError(error, { operation: 'donation.cart.checkout', reason: 'no_cart_defensive' })
      return { status: 'error', error, tracked: true }
    }

    const response = body.guest_identity_id
      ? await current.guestCheckoutMy({
          gateway_id: 'stripe',
          gateway_data: body.gateway_data,
          guest_identity_id: body.guest_identity_id,
          ...(body.captcha ? { captcha: body.captcha } : {}),
        })
      : await current.checkoutMy({
          gateway_id: 'stripe',
          gateway_data: body.gateway_data,
        })

    const result = mapCheckoutResponse(response)

    if (result.status === 'error') {
      await reconcileCartAfterFailure(result.error)
      trackError(result.error, { operation: 'donation.cart.checkout', reason: 'checkout_failure' })
      return { ...result, tracked: true }
    }

    return result
  }

  function trackError<C>(error: Error, ctx?: TrackContext<C>): void {
    errorTracker.track(error, { cart: getCartTrackableData(cart.value), ...ctx })
  }

  return {
    cart,
    pending,
    error,
    amountInvalid,
    selection: optimisticSelection,
    validateForm,
    onSelectPreset,
    onCustomAmountUpdate,
    checkoutPreflight,
    checkoutCart,
  }
}
